import { useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/auth-context'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert } from '@/components/ui/alert'
import { googleIcon as GoogleIcon } from '@/components/icons'

export function LoginPage() {
  const { user, signInWithGoogle } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const location = useLocation()

  const from = location.state?.from?.pathname || '/'

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true)
      setError('')
      await signInWithGoogle()
      // The redirect will be handled by the auth callback
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in')
    } finally {
      setIsLoading(false)
    }
  }

  // Redirect if already authenticated
  if (user) {
    return <Navigate to={from} replace />
  }

  return (
    <div className="min-h-screen flex flex-col bg-background p-4 sm:p-6 md:p-8">
      {/* Logo + Name - Top Left */}
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <img src="/Calintent-transparent-light-teal-500.png" alt="Calintent" className="h-8 w-8 sm:h-10 sm:w-10" />
        <span className="text-lg sm:text-xl font-bold font-jakarta">Calintent</span>
      </div>

      {/* Centered Card */}
      <div className="flex-1 flex items-start sm:items-center justify-center pt-4 sm:pt-0">
        <Card className="w-full max-w-2xl p-4 sm:p-6 md:p-8 flex flex-col">
          {/* Title - Top & Centered */}
          <CardHeader className="text-center p-0 pb-3 sm:pb-4">
            <CardTitle className="text-2xl sm:text-3xl font-bold font-jakarta">
              Calendar automations and analytics
            </CardTitle>
          </CardHeader>
          
          {/* Description */}
          <div className="px-2 sm:px-6 pb-4 sm:pb-6">
            <p className="text-base sm:text-lg leading-relaxed text-foreground text-center">
              A tangible experience of time.<br />
              Visible, measurable and intentionally designed around what truly matters.
            </p>
          </div>
          
          {/* Button */}
          <CardContent className="space-y-3 sm:space-y-4 p-0">
            {error && (
              <Alert variant="destructive">
                {error}
              </Alert>
            )}
            
            <div className="flex justify-center">
              <Button
                variant="default"
                className="w-full sm:w-64 h-11 sm:h-12 text-base font-semibold gap-3"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
              >
                <span className="flex items-center justify-center h-7 w-7 bg-white rounded-sm p-1">
                  <GoogleIcon />
                </span>
                {isLoading ? 'Signing in...' : 'Continue with Google'}
              </Button>
            </div>

            <div className="text-center text-xs sm:text-sm text-muted-foreground">
              <p>
                By continuing with Google, you agree to Calintent's{' '}
                <a 
                  href="https://calintent.com/terms" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-bold text-primary hover:text-primary/80 transition-colors"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a 
                  href="https://calintent.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-bold text-primary hover:text-primary/80 transition-colors"
                >
                  Privacy Policy
                </a>.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
